import api from "./api";

// Shared in-memory cache for /services so navigation between pages is instant.
// Any admin mutation should call invalidateServicesCache() after a successful write.

const TTL_MS = 5 * 60 * 1000; // 5 min soft TTL — after this, next reader gets a fresh copy

let cache = null;
let cachedAt = 0;
let pending = null;

export async function getServices({ force = false } = {}) {
    const now = Date.now();
    if (!force && cache && (now - cachedAt) < TTL_MS) {
        return cache;
    }
    if (pending) return pending;
    pending = api.get("/services")
        .then((r) => {
            cache = Array.isArray(r.data) ? r.data : [];
            cachedAt = Date.now();
            pending = null;
            warmServiceImages(cache);
            return cache;
        })
        .catch((e) => { pending = null; throw e; });
    return pending;
}

// Warm the browser's HTTP image cache for the first few service thumbnails so
// they render instantly when the user finally scrolls to the services grid.
// Uses <link rel="preload" as="image"> for high-priority hinting and falls back
// to a plain Image() decode for older browsers. Idempotent — same URL twice
// still only downloads once (browser handles dedup).
let warmedUrls = new Set();
function warmServiceImages(services, count = 4, width = 480) {
    if (typeof document === "undefined") return; // SSR safety
    const head = document.head;
    if (!head) return;
    const targets = (services || [])
        .filter((s) => s && s.image && s.featured !== false)
        .slice(0, count);
    for (const s of targets) {
        const url = optimizeImageUrl(s.image, width);
        if (!url || warmedUrls.has(url)) continue;
        warmedUrls.add(url);
        try {
            const link = document.createElement("link");
            link.rel = "preload";
            link.as = "image";
            link.href = url;
            link.setAttribute("fetchpriority", "high");
            link.setAttribute("data-warmed", "service");
            head.appendChild(link);
            // Also kick off a decode via Image() — some browsers ignore the
            // preload hint but still cache the network fetch, and this keeps
            // it reliable across engines.
            const img = new Image();
            img.decoding = "async";
            img.src = url;
        } catch {
            /* noop */
        }
    }
}

export function invalidateServicesCache() {
    cache = null;
    cachedAt = 0;
    pending = null;
    warmedUrls = new Set();
}

// Optimise Unsplash / Pexels image URLs — cards are ~320px wide so we can
// safely request smaller assets. Local base64 and other URLs are passed through.
export function optimizeImageUrl(url, targetWidth = 480) {
    if (!url || typeof url !== "string") return url;
    if (url.startsWith("data:")) return url;
    try {
        const u = new URL(url);
        const host = u.hostname;
        if (host.includes("unsplash.com")) {
            u.searchParams.set("auto", "format");
            u.searchParams.set("fit", "crop");
            u.searchParams.set("w", String(targetWidth));
            u.searchParams.set("q", "70");
            return u.toString();
        }
        if (host.includes("pexels.com")) {
            u.searchParams.set("auto", "compress");
            u.searchParams.set("cs", "tinysrgb");
            u.searchParams.set("w", String(targetWidth));
            return u.toString();
        }
    } catch {
        return url;
    }
    return url;
}
